<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import BaseLabel from '@/atoms/BaseLabel.vue';
import BaseButton from '@/atoms/BaseButton.vue';
import CircleIcon from '@/atoms/CircleIcon.vue';
import BottomButtons from '@/atoms/BottomButtons.vue';

export type EndFlowScreenProps = {
  illustrationName?: string;
  title: string;
  description?: string;
  buttonText: string;
  secondaryButtonText?: string;
  showCloseButton?: boolean;
  redirectTo?: string;
};

const props = withDefaults(defineProps<EndFlowScreenProps>(), {
  showCloseButton: true,
});

const emit = defineEmits<{
  buttonClick: [];
  secondaryButtonClick: [];
  close: [];
}>();

const router = useRouter();

const IllustrationComponent = computed(() =>
  props.illustrationName
    ? defineAsyncComponent(() => import(`../assets/illustrations/${props.illustrationName}.svg`))
    : null,
);

const navigate = () => {
  if (props.redirectTo) {
    router.push(props.redirectTo);
  }
};

const handleClose = () => {
  emit('close');
  navigate();
};

const handleButtonClick = () => {
  emit('buttonClick');
  navigate();
};
</script>

<template>
  <div class="min-h-screen w-full" style="background-image: url('/colored-background.png')">
    <div
      class="flex flex-col justify-between mx-4 min-h-screen-safe overflow-y-scroll p-safe text-center relative"
    >
      <div v-if="showCloseButton" class="self-stretch px-4 mt-4 flex justify-end items-center">
        <CircleIcon
          class="cursor-pointer"
          icon-name="x"
          size="medium"
          variant="white"
          @click="handleClose"
          @close="handleClose"
        />
      </div>

      <div class="flex-1 flex flex-col justify-center items-center gap-8 overflow-hidden">
        <component :is="IllustrationComponent" v-if="IllustrationComponent" class="w-24 h-24" />

        <div class="flex flex-col gap-3">
          <BaseLabel :text="title" color="primary" preset="h3" weight="medium" />
          <BaseLabel
            v-if="description"
            :text="description"
            color="secondary"
            preset="medium"
            weight="regular"
          />
        </div>
      </div>

      <BottomButtons :fixed="false" type="vertical">
        <BaseButton
          :label="buttonText"
          class="self-stretch"
          size="medium"
          type="primary"
          @click="handleButtonClick"
        />
        <BaseButton
          v-if="secondaryButtonText"
          :label="secondaryButtonText"
          class="self-stretch"
          size="medium"
          type="secondary"
          @click="emit('secondaryButtonClick')"
        />
      </BottomButtons>
    </div>
  </div>
</template>
